import {
  isApplicationStatusHidden,
  isOnboardedDev,
} from '@internal/components/app/constants';
import { useProfileStrength } from '@internal/components/app/hooks';
import { CandidateRole, useRoleContext } from '@internal/components/app/roles';
import {
  getApplicationHref,
  getApplicationState,
  isAppliedState,
} from '@internal/components/projects/helper/application-state';
import { PROJECT_FROM_QUERY_KEY } from '@internal/components/projects/helper/projects.helpers';
import {
  ROLE_QUERY_KEY,
  RolesTab,
  RoleStart,
  RoleStep,
  START_QUERY_KEY,
} from '@internal/components/roles-hub/roles-hub.types';
import Routes from '@internal/config/routes';
import { Profile } from '@internal/types/profile';
import { Project } from '@internal/types/projects';

import {
  ApplicationTarget,
  ApplicationViewModel,
  buildApplicationViewModel,
  getRoleProgress,
  RoleProgress,
} from './application-progress';

export const getApplicationTargetHref = (
  target: ApplicationTarget,
  project: Project,
  roleId?: string | null
): string => {
  switch (target) {
    case ApplicationTarget.Projects:
      return Routes.OpenProjects;
    case ApplicationTarget.Profile:
      return Routes.Profile;
    case ApplicationTarget.Roles:
      return roleId
        ? `${Routes.Roles}?${ROLE_QUERY_KEY}=${encodeURIComponent(roleId)}`
        : Routes.Roles;
    case ApplicationTarget.RolesExplore:
      return roleId
        ? `${Routes.Roles}?tab=${
            RolesTab.Explore
          }&${ROLE_QUERY_KEY}=${encodeURIComponent(roleId)}`
        : `${Routes.Roles}?tab=${RolesTab.Explore}`;
    case ApplicationTarget.Community:
      return Routes.GetSetUp;
    default:
      return getApplicationHref(project);
  }
};

export const getRoleCallHref = (roleId: string, returnTo: string): string =>
  `${Routes.Roles}?${ROLE_QUERY_KEY}=${roleId}&${START_QUERY_KEY}=${
    RoleStart.Call
  }&${PROJECT_FROM_QUERY_KEY}=${encodeURIComponent(returnTo)}`;

interface ApplicationModelResult {
  model: ApplicationViewModel | null;
  roleProgress: RoleProgress | null;
  loading: boolean;
}

export const useApplicationModel = (
  project: Project,
  profile: Profile,
  buildRoleSteps: (role: CandidateRole) => RoleStep[] = () => []
): ApplicationModelResult => {
  const { roles, loading: rolesLoading } = useRoleContext();
  const { complete: profileComplete, loading: strengthLoading } =
    useProfileStrength(profile);
  const profileReady = isOnboardedDev(profile) || profileComplete;

  const state = getApplicationState(project);
  const loading = rolesLoading || strengthLoading;
  if (!state || !isAppliedState(state) || loading) {
    return { model: null, roleProgress: null, loading };
  }

  const roleProgress = getRoleProgress(project, roles);
  const model = buildApplicationViewModel({
    state,
    project,
    profileReady,
    paused: isApplicationStatusHidden(profile),
    onboarded: isOnboardedDev(profile),
    hideRoleActions: profile.role === 'partner developer',
    roleProgress,
    roleSteps: roleProgress.role ? buildRoleSteps(roleProgress.role) : [],
  });

  return { model, roleProgress, loading };
};
